import type { InferRpcDefinition } from "@redotech/rpc/definition";
import { abandonProcessingSession } from "./schema/abandon-processing-session";
import { addUndeclaredItemToProcessingSession } from "./schema/add-undeclared-item-to-processing-session";
import { assignLotToReturnItem } from "./schema/assign-lot-to-return-item";
import { bindSortingTote } from "./schema/bind-sorting-tote";
import { bindSortingToteByScan } from "./schema/bind-sorting-tote-by-scan";
import { closeStationProcessingSession } from "./schema/close-station-processing-session";
import { completeProcessingSession } from "./schema/complete-processing-session";
import { confirmShippingLabelScanReview } from "./schema/confirm-shipping-label-scan-review";
import { createInventoryItem } from "./schema/create-inventory-item";
import { createProcessingSession } from "./schema/create-processing-session";
import { emitWarehouseReturnSortedEvent } from "./schema/emit-warehouse-return-sorted-event";
import { evaluateSpecForItem } from "./schema/evaluate-spec-for-item";
import { findOrCreateInventoryItemForProcessingSession } from "./schema/find-or-create-inventory-item-for-processing-session";
import { generateLicensePlate } from "./schema/generate-license-plate";
import { generateSortingLicensePlate } from "./schema/generate-sorting-license-plate";
import { generateUnknownLicensePlate } from "./schema/generate-unknown-license-plate";
import { getInventoryUnboxingVideoUploadUrl } from "./schema/get-inventory-unboxing-video-upload-url";
import { getItemForReview } from "./schema/get-item-for-review";
import { getLotCandidatesForReturnItem } from "./schema/get-lot-candidates-for-return-item";
import { getOpenStationProcessingSession } from "./schema/get-open-station-processing-session";
import { getPrinters } from "./schema/get-printers";
import { getProcessingReceivingBins } from "./schema/get-processing-receiving-bins";
import { getProcessingReceivingSource } from "./schema/get-processing-receiving-source";
import { getProcessingSession } from "./schema/get-processing-session";
import { getProcessingSessionSortDestinations } from "./schema/get-processing-session-sort-destinations";
import { getReturnForProcessing } from "./schema/get-return-for-processing";
import { getShippingLabelScanReview } from "./schema/get-shipping-label-scan-review";
import { getShippingLabelScans } from "./schema/get-shipping-label-scans";
import { getUnboxingVideoUploadUrl } from "./schema/get-unboxing-video-upload-url";
import {
  completeItemProcessingSession,
  createItemProcessingSession,
  getItemProcessingSession,
  logItemProcessingEvent,
  lookupItemForProcessing,
  searchProductsForItemProcessing,
  updateItemProcessingSession,
} from "./schema/item-processing";
import { listItemReviewFailReasons } from "./schema/list-item-review-fail-reasons";
import { listSortDestinations } from "./schema/list-sort-destinations";
import { logProcessingEvent } from "./schema/log-processing-event";
import { lookupForSorting } from "./schema/lookup-for-sorting";
import { markSessionItemAsMissing } from "./schema/mark-session-item-as-missing";
import { openStationProcessingSession } from "./schema/open-station-processing-session";
import { printLicensePlate } from "./schema/print-license-plate";
import { listEligibleReceivingSourceBins } from "./schema/receiving-source-bin";
import { recordItemReviewFailure } from "./schema/record-item-review-failure";
import { recordItemSort } from "./schema/record-item-sort";
import { recordSessionItemAsReceived } from "./schema/record-session-item-as-received";
import { removeUndeclaredItemFromProcessingSession } from "./schema/remove-undeclared-item-from-processing-session";
import { resolveItemReviewFailures } from "./schema/resolve-item-review-failures";
import { resolveSortingToteByScan } from "./schema/resolve-sorting-tote-by-scan";
import { saveShippingLabelScanReview } from "./schema/save-shipping-label-scan-review";
import { scanSortingTote } from "./schema/scan-sorting-tote";
import { setProcessingSourceReceivingBin } from "./schema/set-processing-source-receiving-bin";
import { setReturnUnboxingVideo } from "./schema/set-return-unboxing-video";
import { setShippingLabelScanTracking } from "./schema/set-shipping-label-scan-tracking";
import { unbindSortingTote } from "./schema/unbind-sorting-tote";
import { updateProcessingSessionItem } from "./schema/update-processing-session-item";
import { uploadEvidencePhoto } from "./schema/upload-evidence-photo";
import { uploadShippingLabelPhoto } from "./schema/upload-shipping-label-photo";
import { verifyPhotoQuick } from "./schema/verify-photo";

export const rpcDefinition = {
  getProcessingReceivingSource,
  getProcessingReceivingBins,
  setProcessingSourceReceivingBin,
  evaluateSpecForItem,
  getReturnForProcessing,
  listEligibleReceivingSourceBins,
  getInventoryUnboxingVideoUploadUrl,
  getUnboxingVideoUploadUrl,
  setReturnUnboxingVideo,
  lookupItemForProcessing,
  searchProductsForItemProcessing,
  createItemProcessingSession,
  getItemProcessingSession,
  updateItemProcessingSession,
  completeItemProcessingSession,
  logItemProcessingEvent,
  getProcessingSession,
  getProcessingSessionSortDestinations,
  createProcessingSession,
  createInventoryItem,
  updateProcessingSessionItem,
  completeProcessingSession,
  abandonProcessingSession,
  addUndeclaredItemToProcessingSession,
  generateLicensePlate,
  findOrCreateInventoryItemForProcessingSession,
  printLicensePlate,
  generateSortingLicensePlate,
  generateUnknownLicensePlate,
  getPrinters,
  listSortDestinations,
  logProcessingEvent,
  lookupForSorting,
  scanSortingTote,
  emitWarehouseReturnSortedEvent,
  openStationProcessingSession,
  getOpenStationProcessingSession,
  closeStationProcessingSession,
  bindSortingTote,
  bindSortingToteByScan,
  resolveSortingToteByScan,
  unbindSortingTote,
  recordItemSort,
  markSessionItemAsMissing,
  recordSessionItemAsReceived,
  removeUndeclaredItemFromProcessingSession,
  uploadEvidencePhoto,
  uploadShippingLabelPhoto,
  getShippingLabelScans,
  getShippingLabelScanReview,
  saveShippingLabelScanReview,
  confirmShippingLabelScanReview,
  setShippingLabelScanTracking,
  verifyPhotoQuick,
  getLotCandidatesForReturnItem,
  assignLotToReturnItem,
  getItemForReview,
  listItemReviewFailReasons,
  recordItemReviewFailure,
  resolveItemReviewFailures,
};

export type RedoReturnsProcessingRpcDefinition = InferRpcDefinition<
  typeof rpcDefinition
>;
